import React, { useRef } from "react";
import { UploadSimple, ImageSquare, MusicNote, Plus, Trash } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";

const SUGGESTED_PLATFORMS = ["Spotify", "Apple Music", "SoundCloud", "Instagram", "TikTok", "Twitter", "YouTube", "Website"];

// Image preview + upload button column.
export function SlotMediaPanel({ rank, imagePath, uploading, onUpload }) {
  const ref = useRef(null);
  return (
    <div className="lg:col-span-3 space-y-2">
      <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">Artwork</label>
      <div className="relative aspect-square rounded-md bg-ink-900 border border-ink-600 overflow-hidden">
        {imagePath ? (
          <img src={fileUrl(imagePath)} alt="" className="w-full h-full object-cover" data-testid={`admin-image-preview-${rank}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-600">
            <ImageSquare size={40} />
          </div>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
        data-testid={`admin-image-input-${rank}`}
      />
      <button
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 transition-colors text-xs"
        data-testid={`admin-image-upload-${rank}`}
      >
        <UploadSimple size={14} weight="bold" />
        {uploading ? "Uploading..." : "Upload image"}
      </button>
    </div>
  );
}

// Title / artist / bio fields column.
export function SlotTextFields({ rank, songTitle, setSongTitle, artistName, setArtistName, bio, setBio }) {
  return (
    <div className="lg:col-span-5 space-y-3">
      <Field label="Song title" testId={`admin-title-${rank}`} value={songTitle} onChange={setSongTitle} />
      <Field label="Artist" testId={`admin-artist-${rank}`} value={artistName} onChange={setArtistName} />
      <div>
        <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Bio</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 resize-y"
          data-testid={`admin-bio-${rank}`}
        />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60"
        data-testid={testId}
      />
    </div>
  );
}

// Audio preview + upload + dynamic social links list column.
export function SlotMetaPanel({ rank, audioPath, isrc, uploadingAudio, onAudioUpload, links, setLinks, videoUrl, setVideoUrl }) {
  const audRef = useRef(null);

  const addLink = () => setLinks([...links, { platform: "Spotify", url: "", _key: `new-${Date.now()}-${Math.random()}` }]);
  const updateLink = (i, field, value) => {
    const next = [...links];
    next[i] = { ...next[i], [field]: value };
    setLinks(next);
  };
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  return (
    <div className="lg:col-span-4 space-y-3">
      <div>
        <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Audio snippet (1–2 min)</label>
        <div className="flex items-center gap-2 mb-2">
          {audioPath ? (
            <audio controls src={fileUrl(audioPath)} className="w-full h-9" data-testid={`admin-audio-preview-${rank}`} />
          ) : (
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-zinc-500 text-xs">
              <MusicNote size={14} /> No audio
            </div>
          )}
        </div>
        <input
          ref={audRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onAudioUpload(e.target.files[0])}
          data-testid={`admin-audio-input-${rank}`}
        />
        <button
          onClick={() => audRef.current?.click()}
          disabled={uploadingAudio}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 transition-colors text-xs"
          data-testid={`admin-audio-upload-${rank}`}
        >
          <UploadSimple size={14} weight="bold" />
          {uploadingAudio ? "Uploading..." : "Upload audio"}
        </button>
        {isrc && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] font-mono text-neon-cyan bg-neon-cyan/5 border border-neon-cyan/25 rounded px-2 py-1" data-testid={`admin-isrc-${rank}`}>
            <span className="text-zinc-400 uppercase tracking-widest">ISRC</span>
            <span className="tracking-widest">{isrc}</span>
            <button
              type="button"
              onClick={async () => {
                try { await navigator.clipboard.writeText(isrc); } catch (_e) { void _e; }
              }}
              className="ml-auto text-zinc-500 hover:text-neon-cyan"
              title="Copy ISRC"
              data-testid={`admin-isrc-copy-${rank}`}
              aria-label="Copy ISRC"
            >
              <span className="text-[10px]">Copy</span>
            </button>
          </div>
        )}
      </div>

      {setVideoUrl && (
        <div>
          <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">
            Video URL (YouTube / Vimeo / .mp4)
          </label>
          <input
            value={videoUrl || ""}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
            className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-xs font-mono focus:outline-none focus:border-neon-cyan/60"
            data-testid={`admin-video-url-${rank}`}
          />
          <p className="mt-1 text-[10px] text-zinc-600">Opens in a lightbox on the public site.</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">Social links</label>
          <button onClick={addLink} className="text-neon-cyan text-xs inline-flex items-center gap-1 hover:text-neon-cyanHover" data-testid={`admin-add-link-${rank}`}>
            <Plus size={12} weight="bold" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {links.length === 0 && <p className="text-xs text-zinc-600">No links yet.</p>}
          {links.map((l, i) => (
            <div key={l._key || `${rank}-link-${i}`} className="flex gap-1.5" data-testid={`admin-link-row-${rank}-${i}`}>
              <input
                list={`platforms-${rank}`}
                value={l.platform}
                onChange={(e) => updateLink(i, "platform", e.target.value)}
                placeholder="Platform"
                className="w-28 px-2 py-1.5 rounded-md bg-ink-900 border border-ink-600 text-white text-xs focus:outline-none focus:border-neon-cyan/60"
              />
              <input
                value={l.url}
                onChange={(e) => updateLink(i, "url", e.target.value)}
                placeholder="https://..."
                className="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-ink-900 border border-ink-600 text-white text-xs focus:outline-none focus:border-neon-cyan/60"
              />
              <button onClick={() => removeLink(i)} className="px-2 text-zinc-500 hover:text-neon-magenta transition-colors" data-testid={`admin-remove-link-${rank}-${i}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          <datalist id={`platforms-${rank}`}>
            {SUGGESTED_PLATFORMS.map((p) => <option key={p} value={p} />)}
          </datalist>
        </div>
      </div>
    </div>
  );
}
